import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as PieTooltip } from 'recharts';
import styles, { rechartsStyles } from './HomePieChart.styles';
import { buildHomePieData } from '../../utils/home.helpers';

const CHART_COLORS = ['#5236AB', '#EA6361'];
const LEGEND_ENTRIES = [
  { label: 'Success', color: CHART_COLORS[0] },
  { label: 'Failure', color: CHART_COLORS[1] },
];

const InlineLegend = React.memo(function InlineLegend() {
  return (
    <Box sx={styles.inlineLegend}>
      {LEGEND_ENTRIES.map((e) => (
        <Box key={e.label} sx={styles.inlineLegendItem}>
          <Box sx={{ ...styles.inlineLegendSwatch, backgroundColor: e.color }} />
          <Typography variant="caption" sx={styles.inlineLegendText}>
            {e.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
});

export default function HomePieChart({ fileStats }) {
  const { pieDecimals, displayMap } = useMemo(() => buildHomePieData(fileStats), [fileStats]);
  const hasData = pieDecimals.length > 0;

  return (
    <Box sx={{ ...styles.secondCard, ...styles.chartRoot, ...styles.chartText }}>
      <Box sx={styles.headerRow}>
        <Typography variant="subtitle2" sx={styles.headerTitle}>
          Files Load Rate
        </Typography>
      </Box>

      <Box sx={styles.chartContainer}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={rechartsStyles.margin}>
              <Pie
                data={pieDecimals}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius="80%"
                innerRadius="45%"
                labelLine={false}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, name }) => {
                  const value = displayMap[name] ?? 0;
                  if (!value) return null;
                  const RAD = Math.PI / 180;
                  const rIn = parseFloat(innerRadius);
                  const rOut = parseFloat(outerRadius);
                  const radius = rIn + (rOut - rIn) * 0.5;
                  const x = cx + radius * Math.cos(-midAngle * RAD);
                  const y = cy + radius * Math.sin(-midAngle * RAD);
                  return (
                    <text
                      x={x}
                      y={y}
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="central"
                      style={{ fontSize: '13px' }}
                    >
                      {`${value}%`}
                    </text>
                  );
                }}
              >
                {pieDecimals.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <PieTooltip
                formatter={(v, name) => [`${displayMap[name] ?? Math.round(v)}%`, name]}
                wrapperStyle={rechartsStyles.tooltip}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
            }}
            role="status"
            aria-live="polite"
          >
            <Typography variant="caption" align="center" sx={{ opacity: 0.8 }}>
              No data available
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={styles.inlineLegend}>
        <InlineLegend />
      </Box>
    </Box>
  );
}
